import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { RouteRecordNormalized } from 'vue-router'
import type { EmergencyAuthority } from '@/constants/router/auth'

export interface RefreshState {
  /** リフレッシュ処理が必表かどうか */
  onRefresh: boolean
  error?: string | null
}
interface Attributes {
  [key: string]: string | object
}
interface CognitoAuth {
  isAuthorized: boolean
  menu: RouteRecordNormalized[]
  attributes: undefined | Attributes
  sensitiveInfo: undefined | object
  refreshState: undefined | RefreshState
}

export const useMiiaFwStore = defineStore('miiaFwStore', () => {
  const loadingView = ref({ isLoading: false, message: '' })
  const loadingImage = ref<null | string>(null)
  const cognitoAuth = ref<CognitoAuth>({
    isAuthorized: false,
    menu: [],
    attributes: undefined,
    sensitiveInfo: undefined,
    refreshState: undefined
  })
  const isPermitted = ref<boolean>(false)

  const isLoading = computed(() => loadingView.value.isLoading)
  const loadingMessage = computed(() => loadingView.value.message)
  const isAuthorized = computed(() => cognitoAuth.value.isAuthorized)
  const menu = computed(() => cognitoAuth.value.menu)
  const attributes = computed(() => cognitoAuth.value.attributes)
  const sensitiveInfo = computed(() => cognitoAuth.value.sensitiveInfo)
  const refreshState = computed(() => cognitoAuth.value.refreshState)
  const emergencyAuthority = computed(() => {
    const attribute = cognitoAuth.value.attributes?.attribute as {
      'disaster-emergency'?: { authority: EmergencyAuthority[] }
    }
    if (attribute?.['disaster-emergency']) {
      return attribute['disaster-emergency'].authority
    } else {
      return []
    }
  })

  const enableLoadingView = (argMessage: string) => {
    loadingView.value = { isLoading: true, message: argMessage }
  }
  const disableLoadingView = () => {
    const currentMessage = loadingView.value.message
    loadingView.value = { isLoading: false, message: currentMessage }
  }
  const updateLoadingImage = (imageData: string) => {
    loadingImage.value = imageData
  }
  const updateAuthInfo = (isAuthorized: boolean) => {
    cognitoAuth.value.isAuthorized = isAuthorized
  }
  const updateIsAuthorized = (isAuthorized: boolean) => {
    cognitoAuth.value.isAuthorized = isAuthorized
  }
  const updateMenu = (menu: RouteRecordNormalized[]) => {
    if (Array.isArray(menu)) {
      cognitoAuth.value.menu = menu
    }
  }
  const updateAttributes = (attributes: { [key: string]: string } | undefined) => {
    cognitoAuth.value.attributes = attributes
  }
  const updateSensitiveInfo = (obj: { [key: string]: string } | undefined) => {
    cognitoAuth.value.sensitiveInfo = obj
  }
  const updateRefreshState = (obj: RefreshState) => {
    cognitoAuth.value.refreshState = obj
  }
  const updateIsPermitted = (permitted: boolean) => {
    isPermitted.value = permitted
  }

  const reset = () => {
    loadingView.value = { isLoading: false, message: '' }
    loadingImage.value = null
    cognitoAuth.value = {
      isAuthorized: false,
      menu: [],
      attributes: undefined,
      sensitiveInfo: undefined,
      refreshState: undefined
    }
    isPermitted.value = false
  }

  return {
    loadingView,
    loadingImage,
    cognitoAuth,
    isPermitted,
    isLoading,
    loadingMessage,
    isAuthorized,
    menu,
    attributes,
    sensitiveInfo,
    refreshState,
    emergencyAuthority,
    updateAuthInfo,
    updateIsAuthorized,
    enableLoadingView,
    disableLoadingView,
    updateLoadingImage,
    updateMenu,
    updateAttributes,
    updateSensitiveInfo,
    updateRefreshState,
    updateIsPermitted,
    reset
  }
})
